import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { t } from "../i18n";
import MediaSelectorMini from "./MediaSelectorMini.jsx";
import "./SegmentCard.css";

/**
 * SegmentCard - Composant pour afficher et éditer un segment de vidéo
 *
 * Props:
 * - segment: object - Données du segment
 * - index: number - Index du segment dans la liste
 * - onUpdate: function - Callback pour mettre à jour le segment
 * - onDelete: function - Callback pour supprimer le segment
 * - onRegenerate: function - Callback pour régénérer le segment
 * - onEnhancePrompt: function - Callback pour améliorer le prompt
 * - readonly: boolean - Mode lecture seule
 * - draggable: boolean - Allow drag and drop
 * - onDragStart: function - Drag start handler
 * - onDragOver: function - Drag over handler
 * - onDragLeave: function - Drag leave handler
 * - onDrop: function - Drop handler
 * - onDragEnd: function - Drag end handler
 * - isDragging: boolean - Is this segment being dragged
 * - isDragOver: boolean - Is another segment being dragged over this one
 */
const SegmentCard = forwardRef(function SegmentCard({
  segment,
  index,
  prevSegment,
  onUpdate,
  onSaveSegment,
  onDelete,
  onRegenerate,
  onEnhancePrompt,
  onGenerateStartImage,
  onGenerateEndImage,
  onGenerateVideo,
  onAddReference,
  onDirtyChange,
  montageId = "",
  aspectRatio = "",
  showEndImage = true,
  forceEdit = false,
  saving = false,
  readonly = false,
  draggable = false,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  isDragging = false,
  isDragOver = false
}, ref) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedSegment, setEditedSegment] = useState(segment);
  const [uploadingImage, setUploadingImage] = useState(null); // "start" or "end"
  const [imageWarnings, setImageWarnings] = useState({});
  const editing = forceEdit || isEditing;

  function checkImageRatio(url, imageType) {
    if (!aspectRatio || !url) return;
    const [rw, rh] = aspectRatio.split(":").map(Number);
    if (!rw || !rh) return;
    const expectedRatio = rw / rh;
    const img = new Image();
    img.onload = () => {
      const actualRatio = img.naturalWidth / img.naturalHeight;
      const tolerance = 0.15;
      if (Math.abs(actualRatio - expectedRatio) / expectedRatio > tolerance) {
        const labelMap = { landscape: t("segment.landscape"), portrait: t("segment.portrait"), square: t("segment.square") };
        const actualKey = actualRatio > 1 ? "landscape" : actualRatio < 1 ? "portrait" : "square";
        const expectedKey = expectedRatio > 1 ? "landscape" : expectedRatio < 1 ? "portrait" : "square";
        let msg = `${t("segment.format")} ${img.naturalWidth}x${img.naturalHeight}`;
        if (actualKey !== expectedKey) {
          msg += ` (${labelMap[actualKey]}) — ${t("segment.montageIs")} ${aspectRatio} (${labelMap[expectedKey]})`;
        } else {
          msg += ` — ${t("segment.ratioDiffers")} (${aspectRatio})`;
        }
        setImageWarnings((prev) => ({ ...prev, [imageType]: msg }));
      } else {
        setImageWarnings((prev) => {
          const next = { ...prev };
          delete next[imageType];
          return next;
        });
      }
    };
    img.src = url;
  }
  const hasChanges = JSON.stringify(editedSegment) !== JSON.stringify(segment);

  useEffect(() => {
    onDirtyChange?.(hasChanges);
  }, [hasChanges, onDirtyChange]);

  useImperativeHandle(ref, () => ({
    save: () => handleSave(),
    cancel: () => handleCancel(),
    hasChanges,
  }));

  useEffect(() => {
    setEditedSegment(segment);
  }, [segment]);

  // Extract prompt data
  const promptText = typeof segment.prompt === "string"
    ? segment.prompt
    : segment.prompt?.text || "";
  const promptEnhanced = typeof segment.prompt === "string"
    ? ""
    : segment.prompt?.enhanced || "";
  const promptTags = segment.prompt?.tags || [];

  const startImagePrompt = segment.start_image?.prompt || "";
  const endImagePrompt = segment.end_image?.prompt || "";
  const startImageStatus = segment.start_image?.status || "pending";
  const endImageStatus = segment.end_image?.status || "pending";
  const videoStatus = segment.video?.status || "pending";
  const prevEndUrl = prevSegment?.end_image?.url || "";
  const canUsePrevEnd = Boolean(prevEndUrl) && index > 0;

  // Handle field updates
  const handleFieldChange = (field, value) => {
    setEditedSegment(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePromptChange = (field, value) => {
    setEditedSegment(prev => {
      const basePrompt = typeof prev.prompt === "string"
        ? { text: prev.prompt, tags: [] }
        : prev.prompt || {};

      return {
        ...prev,
        prompt: { ...basePrompt, [field]: value }
      };
    });
  };

  const handleImagePromptChange = (imageType, value) => {
    setEditedSegment(prev => ({
      ...prev,
      [imageType]: {
        ...prev[imageType],
        prompt: value
      }
    }));
  };

  const handleUsePrevEnd = (checked) => {
    if (!checked) {
      setEditedSegment(prev => ({
        ...prev,
        start_image: {
          ...prev.start_image,
          url: null,
          status: "pending",
          type: "to_generate"
        }
      }));
      onUpdate({
        ...segment,
        start_image: {
          ...segment.start_image,
          url: null,
          status: "pending",
          type: "to_generate"
        }
      });
      return;
    }
    if (!prevEndUrl) return;
    setEditedSegment(prev => ({
      ...prev,
      start_image: {
        ...prev.start_image,
        url: prevEndUrl,
        status: "completed",
        type: "linked_prev_end"
      }
    }));
    onUpdate({
      ...segment,
      start_image: {
        ...segment.start_image,
        url: prevEndUrl,
        status: "completed",
        type: "linked_prev_end"
      }
    });
  };

  // Save changes
  const handleSave = () => {
    onUpdate(editedSegment);
    if (onSaveSegment) {
      onSaveSegment(editedSegment);
    }
    if (!forceEdit) {
      setIsEditing(false);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setEditedSegment(segment);
    setIsEditing(false);
  };

  // Delete segment
  const handleDeleteClick = () => {
    if (window.confirm(`${t("segment.confirmDelete")} ${index + 1} ?`)) {
      onDelete();
    }
  };

  // Status icons
  const getStatusIcon = (status) => {
    switch (status) {
      case "pending": return "hourglass_empty";
      case "generating": return "autorenew";
      case "completed": return "check_circle";
      case "error": return "error";
      default: return "hourglass_empty";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pending": return "En attente";
      case "generating": return "Génération...";
      case "completed": return "Terminé";
      case "error": return "Erreur";
      default: return "En attente";
    }
  };

  // Handle regenerate
  const handleRegenerateClick = () => {
    if (onRegenerate && window.confirm(`${t("segment.confirmRegenerate")} ${index + 1} ?`)) {
      onRegenerate();
    }
  };

  // Handle enhance prompt
  const handleEnhancePromptClick = () => {
    if (onEnhancePrompt) {
      onEnhancePrompt();
    }
  };

  // Handle image upload
  const handleImageUpload = async (imageType, file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert(t("segment.invalidFileType"));
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert(t("segment.fileTooLarge"));
      return;
    }

    setUploadingImage(imageType);

    try {
      // Create form data
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "images");

      // Upload file
      const res = await fetch("/api/fast/upload", {
        method: "POST",
        body: formData
      });

      const data = await res.json();

      if (!data.ok || !data.url) {
        throw new Error(data.error || "Erreur upload");
      }

      checkImageRatio(data.url, imageType);

      // Update segment with uploaded image
      setEditedSegment(prev => ({
        ...prev,
        [imageType]: {
          ...prev[imageType],
          url: data.url,
          status: "completed",
          type: "uploaded"
        }
      }));

      // Auto-save
      const updated = {
        ...editedSegment,
        [imageType]: {
          ...editedSegment[imageType],
          url: data.url,
          status: "completed",
          type: "uploaded"
        }
      };
      onUpdate(updated);

    } catch (err) {
      alert(err.message || t("segment.uploadError"));
      console.error("Upload error:", err);
    } finally {
      setUploadingImage(null);
    }
  };

  // Handle image file input
  const handleImageFileChange = (imageType, event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(imageType, file);
    }
  };

  // Clear uploaded image
  const handleClearImage = (imageType) => {
    setEditedSegment(prev => ({
      ...prev,
      [imageType]: {
        ...prev[imageType],
        url: null,
        status: "pending",
        type: "to_generate"
      }
    }));
    setImageWarnings((prev) => {
      const next = { ...prev };
      delete next[imageType];
      return next;
    });
  };

  return (
    <div
      className={`segment-card ${isEditing ? "editing" : ""} ${isDragging ? "dragging" : ""} ${isDragOver ? "drag-over" : ""}`}
      draggable={draggable && !isEditing}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      {/* Header */}
      <div className="segment-card-header">
        <div className="segment-card-title">
          <span className="segment-card-badge">{index + 1}</span>
          <span className="segment-card-name">Segment {index + 1}</span>
        </div>

        <div className="segment-card-meta">
          <div className="segment-card-duration">
            <span className="segment-inline-meta">
              <span className="material-symbols-rounded segment-inline-icon">schedule</span>
              {segment.duration}s
            </span>
          </div>

          {!readonly && (
            <div className="segment-card-actions">
              {!editing ? (
                <>
                  {draggable && (
                    <button
                      type="button"
                      className="segment-btn segment-btn-drag"
                      title="Glisser-déposer"
                      style={{ cursor: "grab" }}
                    >
                      <span className="material-symbols-rounded">drag_indicator</span>
                    </button>
                  )}
                  {onEnhancePrompt && (
                    <button
                      type="button"
                      onClick={handleEnhancePromptClick}
                      className="segment-btn segment-btn-enhance"
                      title="Améliorer le prompt"
                    >
                      <span className="material-symbols-rounded">auto_fix_high</span>
                    </button>
                  )}
                  {onRegenerate && (
                    <button
                      type="button"
                      onClick={handleRegenerateClick}
                      className="segment-btn segment-btn-regenerate"
                      title={t("segment.card.regenerate")}
                    >
                      <span className="material-symbols-rounded">refresh</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="segment-btn segment-btn-edit"
                    title={t("segment.card.edit")}
                  >
                    <span className="material-symbols-rounded">edit</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteClick}
                    className="segment-btn segment-btn-delete"
                    title={t("segment.card.delete")}
                  >
                    <span className="material-symbols-rounded">delete</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="segment-btn segment-btn-save"
                    disabled={!hasChanges}
                    title={t("segment.card.save")}
                  >
                    <span className="material-symbols-rounded">check</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="segment-btn segment-btn-cancel"
                    title={t("segment.card.cancel")}
                  >
                    <span className="material-symbols-rounded">close</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="segment-card-body">
        {/* Duration */}
        {editing && (
          <div className="segment-card-section segment-duration-section">
            <label className="segment-card-label">
              <span className="material-symbols-rounded segment-label-icon">schedule</span>
              {t("segment.card.duration")}
            </label>
            <div className="segment-duration-row">
              <input
                type="range"
                min={5}
                max={15}
                value={editedSegment.duration || 10}
                onChange={(e) => handleFieldChange("duration", parseInt(e.target.value) || 10)}
                className="segment-duration-slider"
              />
              <div className="segment-duration-value">
                <input
                  type="number"
                  value={editedSegment.duration || 10}
                  onChange={(e) => handleFieldChange("duration", Math.min(15, Math.max(5, parseInt(e.target.value) || 5)))}
                  min={5}
                  max={15}
                  className="segment-duration-input"
                />
                <span className="segment-duration-unit">sec</span>
              </div>
            </div>
          </div>
        )}

        {/* Main prompt */}
        <div className="segment-card-section">
          <label className="segment-card-label">
            <span className="material-symbols-rounded segment-label-icon">movie</span>
            {t("segment.description")}
          </label>
          {editing ? (
            <>
              <textarea
                value={typeof editedSegment.prompt === "string" ? editedSegment.prompt : (editedSegment.prompt?.text || "")}
                onChange={(e) => handlePromptChange("text", e.target.value)}
                rows={3}
                className="segment-textarea"
                placeholder={t("segment.promptPlaceholder")}
              />

              {/* Enhanced Prompt (editable) */}
              {(editedSegment.prompt?.enhanced) && (
                <div style={{ marginTop: "10px" }}>
                  <label className="segment-card-label" style={{ color: "rgba(197, 160, 89, 0.9)", marginBottom: "6px" }}>
                    <span className="material-symbols-rounded segment-label-icon">auto_awesome</span>
                    {t("segment.card.enhancedPrompt")}
                  </label>
                  <textarea
                    value={editedSegment.prompt.enhanced}
                    onChange={(e) => handlePromptChange("enhanced", e.target.value)}
                    rows={3}
                    className="segment-textarea segment-textarea-enhanced"
                    placeholder={t("segment.card.enhancedPrompt")}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <p className="segment-card-text">{promptText}</p>
              {promptEnhanced && (
                <div className="segment-enhanced-prompt">
                  <span className="segment-enhanced-label">✨ {t("segment.card.enhancedPrompt")}</span>
                  <p className="segment-card-text segment-enhanced-text">{promptEnhanced}</p>
                </div>
              )}
            </>
          )}

          {promptTags.length > 0 && !editing && (
            <div className="segment-card-tags">
              {promptTags.map((tag, i) => (
                <span key={i} className="segment-card-tag">{tag}</span>
              ))}
            </div>
          )}

          {editing && (
            <div className="segment-media-actions">
              {onEnhancePrompt && (
                <button
                  type="button"
                  className="segment-media-action-btn"
                  onClick={handleEnhancePromptClick}
                >
                  <span className="material-symbols-rounded">auto_fix_high</span>
                  {t("segment.card.enhance")}
                </button>
              )}
              {onRegenerate && (
                <button
                  type="button"
                  className="segment-media-action-btn"
                  onClick={handleRegenerateClick}
                >
                  <span className="material-symbols-rounded">refresh</span>
                  {t("segment.card.regenerate")}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Start image */}
        <div className="segment-card-section">
          <label className="segment-card-label">
            <span className="material-symbols-rounded segment-label-icon">image</span>
            {t("segment.startImage")}
            <span className="segment-status-badge">
              <span className={`material-symbols-rounded segment-status-icon ${startImageStatus}`}>
                {getStatusIcon(startImageStatus)}
              </span>
            </span>
          </label>
          {editing ? (
            <>
              <label className="segment-inline-toggle">
                <input
                  type="checkbox"
                  disabled={!canUsePrevEnd}
                  checked={segment.start_image?.type === "linked_prev_end"}
                  onChange={(e) => handleUsePrevEnd(e.target.checked)}
                />
                {t("segment.usePrevEnd")}
              </label>
              <textarea
                value={editedSegment.start_image?.prompt || ""}
                onChange={(e) => handleImagePromptChange("start_image", e.target.value)}
                rows={2}
                className="segment-textarea segment-textarea-small"
                placeholder={t("segment.startImagePlaceholder")}
              />

              {/* Upload/Select Media */}
              <div className="segment-media-actions">
                <button
                  type="button"
                  className="segment-media-action-btn"
                  onClick={onGenerateStartImage}
                  disabled={!onGenerateStartImage}
                  title={t("segment.generateStartImage")}
                >
                  <span className="material-symbols-rounded">auto_awesome</span>
                  {t("segment.card.generate")}
                </button>
                <MediaSelectorMini
                  onSelect={(url) => {
                    checkImageRatio(url, "start_image");
                    const updated = {
                      ...editedSegment,
                      start_image: { ...editedSegment.start_image, url, status: "completed", type: "uploaded" }
                    };
                    setEditedSegment(updated);
                    onUpdate(updated);
                  }}
                  uploading={uploadingImage === "start_image"}
                  pickerTarget={montageId ? `segment_start_${segment.segment_id || index}` : ""}
                  pickerReturn={montageId ? `/montage?id=${montageId}` : ""}
                />

                {editedSegment.start_image?.url && (
                  <button
                    type="button"
                    onClick={() => handleClearImage("start_image")}
                    className="segment-media-clear-btn"
                    title={t("segment.deleteImage")}
                  >
                    <span className="material-symbols-rounded">delete</span>
                    {t("segment.card.delete")}
                  </button>
                )}
              </div>

              {editedSegment.start_image?.url && (
                <>
                  <img
                    src={editedSegment.start_image.url}
                    alt="Image de début"
                    className="segment-image-preview"
                  />
                  <button
                    type="button"
                    className="segment-media-action-btn"
                    onClick={() => onAddReference?.(editedSegment.start_image.url)}
                  >
                    <span className="material-symbols-rounded">bookmark_add</span>
                    {t("segment.card.reference")}
                  </button>
                </>
              )}
              {imageWarnings.start_image && (
                <p className="segment-ratio-warning">
                  <span className="material-symbols-rounded">warning</span>
                  {imageWarnings.start_image}
                </p>
              )}
            </>
          ) : (
            <>
              <p className="segment-card-subtext">{startImagePrompt}</p>
              {startImageStatus === "error" && segment.start_image?.error && (
                <p className="segment-error-text">{segment.start_image.error}</p>
              )}
              {segment.start_image?.url && (
                <>
                  <img
                    src={segment.start_image.url}
                    alt="Image de début"
                    className="segment-image-preview"
                  />
                  <button
                    type="button"
                    className="segment-media-action-btn"
                    onClick={() => onAddReference?.(segment.start_image.url)}
                  >
                    <span className="material-symbols-rounded">bookmark_add</span>
                    {t("segment.card.reference")}
                  </button>
                </>
              )}
            </>
          )}
        </div>

        {/* End image — only shown when video quality is "high" */}
        {showEndImage && (
          <div className="segment-card-section">
            <label className="segment-card-label">
              <span className="material-symbols-rounded segment-label-icon">image</span>
              {t("segment.endImage")}
              <span className="segment-status-badge">
                <span className={`material-symbols-rounded segment-status-icon ${endImageStatus}`}>
                  {getStatusIcon(endImageStatus)}
                </span>
              </span>
            </label>
            {editing ? (
              <>
                <textarea
                  value={editedSegment.end_image?.prompt || ""}
                  onChange={(e) => handleImagePromptChange("end_image", e.target.value)}
                  rows={2}
                  className="segment-textarea segment-textarea-small"
                  placeholder={t("segment.endImagePlaceholder")}
                />

                {/* Upload/Select Media */}
                <div className="segment-media-actions">
                  <button
                    type="button"
                    className="segment-media-action-btn"
                    onClick={onGenerateEndImage}
                    disabled={!onGenerateEndImage}
                    title={t("segment.generateEndImage")}
                  >
                    <span className="material-symbols-rounded">auto_awesome</span>
                    {t("segment.card.generate")}
                  </button>
                  <MediaSelectorMini
                    onSelect={(url) => {
                      checkImageRatio(url, "end_image");
                      const updated = {
                        ...editedSegment,
                        end_image: { ...editedSegment.end_image, url, status: "completed", type: "uploaded" }
                      };
                      setEditedSegment(updated);
                      onUpdate(updated);
                    }}
                    uploading={uploadingImage === "end_image"}
                    pickerTarget={montageId ? `segment_end_${segment.segment_id || index}` : ""}
                    pickerReturn={montageId ? `/montage?id=${montageId}` : ""}
                  />

                  {editedSegment.end_image?.url && (
                    <button
                      type="button"
                      onClick={() => handleClearImage("end_image")}
                      className="segment-media-clear-btn"
                      title={t("segment.deleteImage")}
                    >
                      <span className="material-symbols-rounded">delete</span>
                      {t("segment.card.delete")}
                    </button>
                  )}
                </div>

                {editedSegment.end_image?.url && (
                  <>
                    <img
                      src={editedSegment.end_image.url}
                      alt="Image de fin"
                      className="segment-image-preview"
                    />
                    <button
                      type="button"
                      className="segment-media-action-btn"
                      onClick={() => onAddReference?.(editedSegment.end_image.url)}
                    >
                      <span className="material-symbols-rounded">bookmark_add</span>
                      {t("segment.card.reference")}
                    </button>
                  </>
                )}
                {imageWarnings.end_image && (
                  <p className="segment-ratio-warning">
                    <span className="material-symbols-rounded">warning</span>
                    {imageWarnings.end_image}
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="segment-card-subtext">{endImagePrompt}</p>
                {endImageStatus === "error" && segment.end_image?.error && (
                  <p className="segment-error-text">{segment.end_image.error}</p>
                )}
                {segment.end_image?.url && (
                  <>
                    <img
                      src={segment.end_image.url}
                      alt="Image de fin"
                      className="segment-image-preview"
                    />
                    <button
                      type="button"
                      className="segment-media-action-btn"
                      onClick={() => onAddReference?.(segment.end_image.url)}
                    >
                      <span className="material-symbols-rounded">bookmark_add</span>
                      {t("segment.card.reference")}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Video status */}
        {videoStatus !== "pending" && !isEditing && (
          <div className="segment-card-section">
            <label className="segment-card-label">
              <span className="material-symbols-rounded segment-label-icon">videocam</span>
              Vidéo
              <span className="segment-status-badge">
                <span className={`material-symbols-rounded segment-status-icon ${videoStatus}`}>
                  {getStatusIcon(videoStatus)}
                </span>
              </span>
            </label>
            <p className="segment-status-text">{getStatusLabel(videoStatus)}</p>
            {videoStatus === "error" && segment.video?.error && (
              <p className="segment-error-text">{segment.video.error}</p>
            )}
            {segment.video?.url && (
              <video
                src={segment.video.url}
                controls
                className="segment-video-preview"
              />
            )}
          </div>
        )}

        {!isEditing && (
          <div className="segment-card-section">
            <label className="segment-card-label">
              <span className="material-symbols-rounded segment-label-icon">smart_toy</span>
              {t("segment.aiGeneration")}
            </label>
            <div className="segment-media-actions">
              <button
                type="button"
                className="segment-media-action-btn"
                onClick={onGenerateStartImage}
                disabled={!onGenerateStartImage}
              >
                <span className="material-symbols-rounded">image</span>
                {t("segment.startImageShort")}
              </button>
              {showEndImage && (
                <button
                  type="button"
                  className="segment-media-action-btn"
                  onClick={onGenerateEndImage}
                  disabled={!onGenerateEndImage}
                >
                  <span className="material-symbols-rounded">image</span>
                  {t("segment.endImageShort")}
                </button>
              )}
              <button
                type="button"
                className="segment-media-action-btn"
                onClick={onGenerateVideo}
                disabled={!onGenerateVideo}
              >
                <span className="material-symbols-rounded">videocam</span>
                Vidéo
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
});

export default SegmentCard;
